import { App as AntApp, type FormInstance } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { ApiError, toNamePath } from "./apiError.ts";

export type StudentProfile = components["schemas"]["StudentProfile"];

/** Puts a fresh profile into the cache and marks the family pages stale (their student list shows name and status). */
export function useApplyProfile() {
  const queryClient = useQueryClient();
  return async (profile: StudentProfile) => {
    queryClient.setQueryData(["students", profile.id], profile);
    await queryClient.invalidateQueries({ queryKey: ["families"] });
  };
}

/**
 * Shows an API failure: field errors go onto the form fields listed in `fields`, anything else into a toast.
 * Returns nothing; use it as a mutation's onError.
 */
export function useReportError(form?: FormInstance, fields: readonly string[] = []) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  return (err: unknown) => {
    if (!(err instanceof ApiError)) {
      void message.error(t("app.error"));
      return;
    }
    const unmatched: string[] = [];
    const matched: { name: (string | number)[]; errors: string[] }[] = [];
    for (const [path, messages] of Object.entries(err.fieldErrors())) {
      if (form && fields.includes(path)) matched.push({ name: toNamePath(path), errors: messages });
      else unmatched.push(...messages);
    }
    if (form && matched.length > 0) form.setFields(matched);
    if (unmatched.length > 0 || matched.length === 0) {
      void message.error((unmatched.length > 0 ? unmatched : err.messages(t("app.error"))).join(" "));
    }
  };
}
